import { getSupabaseClient } from '../utils/supabaseClient';
import NotificationsService from './NotificationsService';
import { stockStatusOf, resolveReorderPoint, STOCK_STATUS } from '../utils/inventoryStockStatus';

class InventoryAlertsService {
  constructor() {
    this.supabase = getSupabaseClient();
  }

  // Get inventory alerts (low stock and out of stock items)
  async getInventoryAlerts(companyId) {
    try {
      // Get stock from inventory_stock_calculated view (not the empty inventory_stock table)
      // First get all items and locations for this company
      // ARCHIVED ITEMS ARE EXCLUDED. This query had no is_active filter, so the dashboard's
      // "Out of Stock Items" counted the whole catalogue while the Items tab it links to
      // counts the ACTIVE catalogue — and the widget's own button navigates straight to that
      // list. Measured live 2026-09-15 on 48f32d34: 9 items out of stock company-wide, 5 of
      // them active. The dashboard said 9 and the list it opened showed 5, with nothing on
      // screen accounting for the other 4. Testers saw it as 8 against 4, drifting to 7 then
      // 6 within one session as they archived things — the count moved because archiving
      // changed what the LIST showed while the widget kept counting the item.
      //
      // Same rule, and the same reason, as pages/Inventory.js:160. A tile must share both the
      // predicate AND the data scope of the view it opens.
      //
      // This also stops archived items generating fresh out-of-stock NOTIFICATIONS:
      // checkAndCreateAlerts reads these same buckets, so an item you deliberately put away
      // was still alerting that it had run out.
      //
      // `not.is.false` rather than `eq(true)`: is_active is NULLable, and every other surface
      // tests `item.is_active !== false`, which treats NULL as active.
      const { data: itemsData, error: itemsError } = await this.supabase
        .from('inventory_items')
        .select('id, name, sku, reorder_point, unit_of_measure, company_id')
        .eq('company_id', companyId)
        .not('is_active', 'is', false);

      if (itemsError) {
        console.warn(`Inventory alerts temporarily unavailable: ${itemsError.message}`);
        return { outOfStock: [], lowStock: [], inStock: [] };
      }

      const itemMap = {};
      (itemsData || []).forEach(item => {
        itemMap[item.id] = item;
      });
      const itemIds = Object.keys(itemMap);

      if (itemIds.length === 0) {
        return { outOfStock: [], lowStock: [], inStock: [] };
      }

      // Get calculated stock for all items
      const { data: stockData, error: stockError } = await this.supabase
        .from('inventory_stock_calculated')
        .select('item_id, location_id, quantity_on_hand, quantity_available')
        .eq('company_id', companyId)
        .in('item_id', itemIds);

      if (stockError) {
        console.warn(`Inventory alerts temporarily unavailable: ${stockError.message}`);
        return { outOfStock: [], lowStock: [], inStock: [] };
      }


      // Get locations for enrichment
      // (stockData || []) because the early return that guaranteed a non-empty array
      // was removed above -- an empty stock table is now a valid, meaningful state.
      const locationIds = [...new Set((stockData || []).map(s => s.location_id).filter(Boolean))];
      let locationMap = {};
      if (locationIds.length > 0) {
        const { data: locationsData } = await this.supabase
          .from('inventory_locations')
          .select('id, name, type')
          .in('id', locationIds);
        
        (locationsData || []).forEach(loc => {
          locationMap[loc.id] = loc;
        });
      }

      // Aggregate stock by item (sum across all locations).
      //
      // SEEDED FROM THE ITEM LIST, NOT FROM THE STOCK ROWS. This used to build the map
      // by iterating stockData, so an item with no row in inventory_stock_calculated
      // never appeared in it and was silently absent from every bucket -- not counted
      // as out of stock, just gone. Measured live 2026-09-06 on Smoke Test Plumbing:
      // 11 of 54 items have no stock row, so the widget under-reported out-of-stock by
      // exactly those 11.
      //
      // No stock row means no stock. That is the same statement as quantity 0, and it
      // is the case a contractor most needs to see: an item nobody has ever received
      // is precisely the one that is not on the truck. The old early return above --
      // "no stock rows at all, so no alerts" -- was the same mistake at full scale: a
      // company that had never received anything was told everything was fine.
      const aggregatedStock = {};
      Object.keys(itemMap).forEach(itemId => {
        aggregatedStock[itemId] = {
          item_id: itemId,
          quantity_on_hand: 0,
          quantity_available: 0,
          locations: []
        };
      });

      (stockData || []).forEach(stock => {
        const itemId = stock.item_id;
        if (!aggregatedStock[itemId]) {
          aggregatedStock[itemId] = {
            item_id: itemId,
            quantity_on_hand: 0,
            quantity_available: 0,
            locations: []
          };
        }
        aggregatedStock[itemId].quantity_on_hand += stock.quantity_on_hand || 0;
        aggregatedStock[itemId].quantity_available += stock.quantity_available || 0;
        if (stock.location_id && locationMap[stock.location_id]) {
          aggregatedStock[itemId].locations.push(locationMap[stock.location_id]);
        }
      });
      
      // Process stock data to identify alerts
      const alerts = {
        outOfStock: [],
        lowStock: [],
        inStock: []
      };

      Object.values(aggregatedStock).forEach(stock => {
        const item = itemMap[stock.item_id];
        if (!item) return;

        // Classified by utils/inventoryStockStatus, the ONE owner of this rule, because
        // this service was the second implementation and the two had drifted apart in the
        // two ways that file was written to prevent.
        //
        // THRESHOLD. This read `item.reorder_point || 0`, which is named in
        // PRODUCT_DECISIONS.md:1142 as one of the three incompatible guesses that ruling
        // removed: NULL means "not set" and falls back to 5; only an explicit 0 means
        // "never flag this item". Since 20260917_reorder_point_unset_is_null.sql backfilled
        // the 100 unset rows to NULL, `|| 0` gave every one of them a threshold of 0 — so
        // the widget could not see a low-stock item the Inventory page could. Measured live
        // 2026-09-18 on 48f32d34: 103 active items, dashboard 12 out / 2 low / 89 good,
        // Inventory page 12 / 3 / 88. One item, exactly this branch.
        //
        // BASIS. It also read quantity_on_hand where every other surface reasons in
        // AVAILABLE terms (on hand minus reserved). That has not diverged yet on this data,
        // but Jerry's company already carries 100 reserved against 1147 on hand, so it is
        // one fully-reserved item away from doing so.
        const reorderPoint = resolveReorderPoint(item.reorder_point);
        const status = stockStatusOf({
          onHand: stock.quantity_on_hand,
          reserved: stock.quantity_on_hand - stock.quantity_available,
          reorderPoint: item.reorder_point
        });

        // Stock on a truck or at a job site used to be excluded from both alert buckets.
        // Two reasons it is gone. It made the dashboard structurally unable to agree with
        // the Inventory page, which counts those items normally — so this finding was
        // guaranteed to come back the first time a van item ran low (10 vehicle and 2
        // job-site items on 48f32d34 today). And it is backwards for the trade: a van
        // running out of a common part is the reorder trigger, not the case to stay quiet
        // about — it is what Jobber and HouseCall Pro alert on. An out-of-stock mobile
        // item was also being pushed into no bucket at all, so out + low + good quietly
        // stopped summing to the catalogue.
        const alertItem = {
          ...stock,
          item_name: item.name,
          item_sku: item.sku,
          reorder_point: reorderPoint,
          unit_of_measure: item.unit_of_measure || 'each',
          location_name: stock.locations?.map(l => l.name).join(', ') || 'Multiple Locations'
        };

        if (status === STOCK_STATUS.OUT_OF_STOCK) {
          alerts.outOfStock.push(alertItem);
        } else if (status === STOCK_STATUS.LOW_STOCK) {
          alerts.lowStock.push(alertItem);
        } else {
          alerts.inStock.push(alertItem);
        }
      });

      return alerts;
    } catch (error) {
      console.error('Error fetching inventory alerts:', error);
      return { outOfStock: [], lowStock: [], inStock: [] }; // Return empty structure instead of throwing
    }
  }

  // Get summary counts for dashboard
  async getAlertsSummary(companyId) {
    try {
      const alerts = await this.getInventoryAlerts(companyId);
      
      return {
        outOfStockCount: alerts.outOfStock.length,
        lowStockCount: alerts.lowStock.length,
        totalAlertsCount: alerts.outOfStock.length + alerts.lowStock.length,
        inStockCount: alerts.inStock.length
      };
    } catch (error) {
      console.error('Error fetching alerts summary:', error);
      return {
        outOfStockCount: 0,
        lowStockCount: 0,
        totalAlertsCount: 0,
        inStockCount: 0
      };
    }
  }

  // Create notification for inventory alert via NotificationsService
  async createInventoryNotification(companyId, userId, itemId, itemName, currentQuantity, reorderPoint, isOutOfStock = false) {
    return NotificationsService.createNotification(companyId, {
      user_id: userId,
      type: 'INVENTORY',
      title: isOutOfStock ? 'Out of Stock Alert' : 'Low Stock Alert',
      message: isOutOfStock
        ? `${itemName} is out of stock (0 remaining)`
        : `${itemName} stock dropped to ${currentQuantity} (below threshold of ${reorderPoint})`,
      severity: isOutOfStock ? 'CRITICAL' : 'WARNING',
      related_id: itemId,
      data: { currentQuantity, reorderPoint }
    });
  }

  /**
   * Create notifications for items whose stock state has CHANGED.
   *
   * Edge-triggered, not level-triggered. Operator, 2026-09-04: "it shouldn't keep alerting.
   * every day. what if it takes 3 months to replace that item." A cooldown cannot answer that
   * — it only decides how OFTEN to repeat a standing fact. Repeating it at all is the defect:
   * a feed that says the same thing every morning for three months trains people to ignore
   * the feed, which costs you the one alert that mattered.
   *
   * So the alert now fires on the TRANSITION and the persistent state lives where persistent
   * state belongs — the Inventory page's reorder list, which already shows it. This is what
   * PagerDuty, Datadog and Nagios do, and the inventory version of it is what ServiceTitan and
   * Fishbowl do.
   *
   *   OK  -> LOW    one alert
   *   LOW -> OUT    one alert; a real escalation, worth interrupting for
   *   LOW -> LOW    silence, for as long as it lasts — the three-month case
   *   OUT -> LOW    no alert, state updated quietly (it improved)
   *   any -> OK     no alert, state CLEARED, so the next drop alerts again
   *
   * The previous 24h-cooldown version produced 5,052 notifications for EIGHT items in a month,
   * because its "already notified" lookup could never match a row (b52201a7b). Cleared and
   * replaced rather than tuned.
   */
  async checkAndCreateAlerts(companyId, userId) {
    try {
      const alerts = await this.getInventoryAlerts(companyId);

      // The state each item SHOULD be in. inStock is included deliberately and with a null
      // state: recovery is what re-arms the alert, so it has to be processed, not skipped.
      const desired = new Map();
      alerts.outOfStock.forEach(a => desired.set(a.item_id, { state: 'OUT', alert: a }));
      alerts.lowStock.forEach(a => desired.set(a.item_id, { state: 'LOW', alert: a }));
      alerts.inStock.forEach(a => { if (!desired.has(a.item_id)) desired.set(a.item_id, { state: null, alert: a }); });
      if (desired.size === 0) return { alertsChecked: 0, notificationsCreated: 0, newNotifications: [] };

      const itemIds = [...desired.keys()];
      const { data: rows, error } = await this.supabase
        .from('inventory_items')
        .select('id, stock_alert_state')
        .eq('company_id', companyId)
        .in('id', itemIds);

      if (error) {
        console.error('Error reading stock alert state:', error);
        // Fail CLOSED. Without the previous state every item looks like a fresh transition,
        // which is precisely how the old version sent thousands of duplicates.
        return;
      }
      const storedById = Object.fromEntries((rows || []).map(r => [r.id, r.stock_alert_state || null]));

      const newNotifications = [];
      const toClear = [];

      for (const [itemId, { state, alert }] of desired) {
        const stored = storedById[itemId] ?? null;
        if (state === stored) continue;          // nothing changed — stay quiet

        if (state === null) {
          toClear.push(itemId);                  // restocked: re-arm silently
          continue;
        }

        // CLAIM the transition before alerting (investigation 2026-10-25-writes-on-page-view.md
        // item 2). Two runs at once (two tabs, a dashboard load plus a stock movement) both read
        // the old state above, and both used to send the alert. The conditional UPDATE is the
        // claim: Postgres re-checks the WHERE on the locked row, so only one run gets the row
        // back and only that run notifies. Same recipient and same transitions as before.
        const { data: claimed, error: claimError } = await this.supabase
          .from('inventory_items')
          .update({ stock_alert_state: state })
          .eq('id', itemId)
          .eq('company_id', companyId)
          .or(`stock_alert_state.is.null,stock_alert_state.neq.${state}`)
          .select('id');
        if (claimError) {
          // Fail CLOSED, as for the read above: no recorded state, no alert.
          console.error(`Could not record alert state for ${itemId}:`, claimError);
          continue;
        }
        if (!claimed || claimed.length === 0) continue; // another run already alerted

        try {
          await this.createInventoryNotification(
            companyId, userId, itemId, alert.item_name,
            alert.quantity_on_hand ?? alert.quantity, alert.reorder_point,
            state === 'OUT'
          );
          newNotifications.push(alert);
        } catch (err) {
          console.error(`Failed to create notification for item ${alert.item_name}:`, err);
          // Hand the claim back so the next run retries, rather than silently swallowing the
          // one alert the user needed. Only if nobody has moved the state on since.
          const { error: undoError } = await this.supabase
            .from('inventory_items')
            .update({ stock_alert_state: stored })
            .eq('id', itemId)
            .eq('company_id', companyId)
            .eq('stock_alert_state', state);
          if (undoError) console.error(`Could not restore alert state for ${itemId}:`, undoError);
        }
      }

      if (toClear.length > 0) {
        const { error: clearError } = await this.supabase
          .from('inventory_items')
          .update({ stock_alert_state: null })
          .eq('company_id', companyId)
          .in('id', toClear);
        if (clearError) console.error('Could not clear recovered alert states:', clearError);
      }

      return {
        alertsChecked: desired.size,
        notificationsCreated: newNotifications.length,
        newNotifications
      };
    } catch (error) {
      console.error('Error checking and creating alerts:', error);
      throw error;
    }
  }

  /**
   * The badge for one quantity. PRESENTATION only — the rule itself belongs to
   * utils/inventoryStockStatus and is delegated to it, because this was a third copy of
   * the same three-way test and copies drift (the two that already had were what made the
   * dashboard and the Inventory page disagree).
   *
   * `reorderPoint` is the RAW column value, not a pre-resolved number: NULL has to reach
   * resolveReorderPoint to become the default of 5, and every caller used to pre-empt that
   * with `item.reorder_point || 0`, which is the bug.
   */
  getStockStatus(quantity, reorderPoint) {
    const status = stockStatusOf({ onHand: quantity, reserved: 0, reorderPoint });

    if (status === STOCK_STATUS.OUT_OF_STOCK) {
      return {
        status: 'out',
        label: 'Out of Stock',
        color: 'text-red-600',
        bgColor: 'bg-red-100',
        badgeColor: 'bg-red-500'
      };
    } else if (status === STOCK_STATUS.LOW_STOCK) {
      return {
        status: 'low',
        label: 'Low Stock',
        color: 'text-yellow-600',
        bgColor: 'bg-yellow-100',
        badgeColor: 'bg-yellow-500'
      };
    } else {
      return {
        status: 'good',
        label: 'In Stock',
        color: 'text-green-600',
        bgColor: 'bg-green-100',
        badgeColor: 'bg-green-500'
      };
    }
  }
}

export default new InventoryAlertsService();
